// src/components/NotificationBell.jsx
import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabaseClient'

function NotificationBell({ userId, companyId }) {
  const [unreadCount, setUnreadCount] = useState(0)
  const [showDropdown, setShowDropdown] = useState(false)
  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(false)
  const dropdownRef = useRef(null)

  // Fetch notifications
  useEffect(() => {
    if (!userId) return

    const fetchNotifications = async () => {
      setLoading(true)
      try {
        const { data, error } = await supabase
          .from('notifications')
          .select('*')
          .eq('user_id', userId)
          .eq('read', false)  // ← Changed from 'is_read' to 'read'
          .order('created_at', { ascending: false })
          .limit(20)

        if (error) throw error
        setUnreadCount(data?.length || 0)
        setNotifications(data || [])
      } catch (err) {
        console.error('Error fetching notifications:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchNotifications()

    // Subscribe to new notifications
    const subscription = supabase
      .channel('notifications')
      .on('postgres_changes', 
        { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${userId}` },
        () => fetchNotifications()
      )
      .subscribe()

    return () => {
      subscription.unsubscribe()
    }
  }, [userId])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const markAsRead = async (notificationId) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ 
          read: true,  // ← Changed from 'is_read' to 'read'
          read_at: new Date().toISOString() 
        })
        .eq('id', notificationId)
      
      if (error) throw error
      
      setUnreadCount(prev => Math.max(0, prev - 1))
      setNotifications(prev => prev.filter(n => n.id !== notificationId))
    } catch (err) {
      console.error('Error marking notification as read:', err)
    }
  }

  const markAllAsRead = async () => {
    if (notifications.length === 0) return

    try {
      const ids = notifications.map(n => n.id)
      const { error } = await supabase
        .from('notifications')
        .update({ 
          read: true,  // ← Changed from 'is_read' to 'read'
          read_at: new Date().toISOString() 
        })
        .in('id', ids)
      
      if (error) throw error
      
      setUnreadCount(0)
      setNotifications([])
    } catch (err) {
      console.error('Error marking all as read:', err)
    }
  }

  const getNotificationIcon = (type) => {
    const icons = {
      'info': 'ℹ️',
      'lead': '🎯',
      'appointment': '📅',
      'invitation': '📧',
      'contract': '📄',
      'payment': '💳',
      'property': '🏠',
      'success': '✅',
      'warning': '⚠️',
      'error': '❌'
    }
    return icons[type] || '🔔'
  }

  const getNotificationColor = (type) => {
    const colors = {
      'info': '#2c7da0',
      'lead': '#059669',
      'appointment': '#2c7da0',
      'invitation': '#f59e0b',
      'contract': '#7c3aed',
      'payment': '#059669',
      'property': '#1e3a5f',
      'success': '#059669',
      'warning': '#f59e0b',
      'error': '#dc2626'
    }
    return colors[type] || '#64748b'
  }

  if (!userId) {
    return null
  }

  return (
    <div style={{ position: 'relative' }} ref={dropdownRef}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        style={{
          background: 'none',
          border: 'none',
          fontSize: '20px',
          cursor: 'pointer',
          position: 'relative',
          padding: '8px',
          borderRadius: '50%',
          transition: 'background 0.2s'
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.05)'}
        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
        aria-label={`Notifications (${unreadCount} unread)`}
      >
        🔔
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute',
            top: '2px',
            right: '2px',
            background: '#dc2626',
            color: 'white',
            borderRadius: '50%',
            minWidth: '18px',
            height: '18px',
            fontSize: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 'bold',
            padding: '0 5px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {showDropdown && (
        <div style={{
          position: 'absolute',
          top: '45px',
          right: '0',
          width: '360px',
          maxWidth: 'calc(100vw - 20px)',
          background: '#fff',
          borderRadius: '12px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
          zIndex: 1000,
          maxHeight: '450px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Header */}
          <div style={{
            padding: '14px 18px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: '#f8fafc',
            borderRadius: '12px 12px 0 0'
          }}>
            <strong style={{ fontSize: '16px' }}>
              Notifications
              {unreadCount > 0 && (
                <span style={{
                  marginLeft: '8px',
                  background: '#dc2626',
                  color: 'white',
                  borderRadius: '20px',
                  padding: '1px 10px',
                  fontSize: '12px',
                  fontWeight: 'normal'
                }}>
                  {unreadCount} new
                </span>
              )}
            </strong>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#2c7da0',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                  padding: '4px 8px',
                  borderRadius: '6px'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#e2e8f0'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                Mark all as read
              </button>
            )}
          </div>

          {/* Notification List */}
          <div style={{
            overflowY: 'auto',
            flex: 1,
            padding: '4px 0'
          }}>
            {loading ? (
              <div style={{
                padding: '30px',
                textAlign: 'center',
                color: '#64748b'
              }}>
                <div className="spinner" style={{
                  width: '24px',
                  height: '24px',
                  margin: '0 auto 10px',
                  border: '2px solid #e2e8f0',
                  borderTopColor: '#2c7da0',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite'
                }} />
                Loading notifications...
              </div>
            ) : notifications.length === 0 ? (
              <div style={{
                padding: '40px 20px',
                textAlign: 'center',
                color: '#94a3b8'
              }}>
                <div style={{ fontSize: '40px', marginBottom: '10px' }}>🎉</div>
                <p style={{ margin: 0 }}>No new notifications</p>
                <p style={{ fontSize: '13px', marginTop: '4px' }}>You're all caught up!</p>
              </div>
            ) : (
              notifications.map((notification, index) => (
                <div
                  key={notification.id}
                  onClick={() => {
                    if (notification.link) {
                      window.location.href = notification.link
                    }
                    markAsRead(notification.id)
                  }}
                  style={{
                    padding: '12px 16px',
                    borderBottom: index < notifications.length - 1 ? '1px solid #f1f5f9' : 'none',
                    cursor: 'pointer',
                    transition: 'background 0.15s ease',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: `${getNotificationColor(notification.type)}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    flexShrink: 0
                  }}>
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: '14px',
                      color: '#1e293b',
                      lineHeight: '1.4',
                      wordWrap: 'break-word'
                    }}>
                      {notification.message}
                    </div>
                    <div style={{
                      fontSize: '11px',
                      color: '#94a3b8',
                      marginTop: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <span>{new Date(notification.created_at).toLocaleDateString()}</span>
                      <span>•</span>
                      <span>{new Date(notification.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                  </div>
                  {notification.link && (
                    <span style={{
                      fontSize: '12px',
                      color: '#2c7da0',
                      flexShrink: 0
                    }}>
                      →
                    </span>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div style={{
            padding: '10px 16px',
            borderTop: '1px solid #e2e8f0',
            textAlign: 'center',
            background: '#f8fafc',
            borderRadius: '0 0 12px 12px'
          }}>
            <button
              onClick={() => setShowDropdown(false)}
              style={{
                background: 'none',
                border: 'none',
                color: '#94a3b8',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Add the spin animation to your global CSS if not already there */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}

export default NotificationBell